import { cn } from '@/renderer/lib/utils';
import { Invoice } from '@/renderer/types/billing';
import { ArrowUpRight, Download, Filter, Search } from 'lucide-react';
import React, { useDeferredValue, useState } from 'react';

import { BillingStatusBadge } from './BillingStatusBadge';
import { BillingSurface } from './BillingSurface';
import {
  formatBillingDate,
  formatMoney,
  getInvoiceRowLabel,
  getInvoiceStatusTone,
} from './formatters';

type InvoiceFilter = 'all' | 'paid' | 'pending';

interface BillingHistoryTableProps {
  invoices: Invoice[];
}

const filterOptions: Array<{ label: string; value: InvoiceFilter }> = [
  { label: 'All', value: 'all' },
  { label: 'Paid', value: 'paid' },
  { label: 'Pending', value: 'pending' },
];

export const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  invoices,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<InvoiceFilter>('all');
  const deferredSearch = useDeferredValue(searchValue);
  const normalizedSearch = deferredSearch.trim().toLowerCase();

  const sortedInvoices = [...invoices].sort(
    (left, right) =>
      new Date(right.invoice_date).getTime() -
      new Date(left.invoice_date).getTime(),
  );

  const visibleInvoices = sortedInvoices.filter((invoice) => {
    const isPending = invoice.status === 'draft' || invoice.status === 'open';
    const matchesFilter =
      activeFilter === 'all' ||
      (activeFilter === 'paid' && invoice.status === 'paid') ||
      (activeFilter === 'pending' && isPending);

    if (!matchesFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const searchableValue = [
      invoice.status,
      invoice.stripe_invoice_id,
      getInvoiceRowLabel(invoice),
      formatBillingDate(invoice.invoice_date),
    ]
      .join(' ')
      .toLowerCase();

    return searchableValue.includes(normalizedSearch);
  });

  return (
    <BillingSurface className="overflow-hidden" compact>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Billing History
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Search invoices, review statuses, and open hosted receipts.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                aria-label="Search invoices"
                placeholder="Search invoices"
                className="h-10 w-full rounded-2xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 sm:w-60"
              />
            </div>

            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
              {filterOptions.map((option) => {
                const isActive = option.value === activeFilter;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setActiveFilter(option.value)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition',
                      isActive
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    {option.value === 'all' && <Filter size={13} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-y-2">
            <thead>
              <tr>
                <th className="px-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Period
                </th>
                <th className="px-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Invoice Date
                </th>
                <th className="px-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Amount
                </th>
                <th className="px-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Status
                </th>
                <th className="px-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.length > 0 ? (
                visibleInvoices.map((invoice) => (
                  <tr key={invoice.id} className="rounded-2xl">
                    <td className="rounded-l-2xl border-y border-l border-slate-200/80 bg-white px-3 py-3 text-sm font-medium text-slate-900">
                      <div>{getInvoiceRowLabel(invoice)}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {invoice.stripe_invoice_id.slice(-8).toUpperCase()}
                      </div>
                    </td>
                    <td className="border-y border-slate-200/80 bg-white px-3 py-3 text-sm text-slate-600">
                      {formatBillingDate(invoice.invoice_date)}
                    </td>
                    <td className="border-y border-slate-200/80 bg-white px-3 py-3 text-sm font-semibold text-slate-900">
                      {formatMoney(invoice.amount_paid || invoice.amount_due, {
                        currency: invoice.currency.toUpperCase(),
                        fromMinorUnits: true,
                      })}
                    </td>
                    <td className="border-y border-slate-200/80 bg-white px-3 py-3">
                      <BillingStatusBadge
                        label={invoice.status.replace('_', ' ')}
                        tone={getInvoiceStatusTone(invoice.status)}
                      />
                    </td>
                    <td className="rounded-r-2xl border-y border-r border-slate-200/80 bg-white px-3 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                            title="Download invoice PDF"
                          >
                            <Download size={15} />
                          </a>
                        )}
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                            title="Open hosted invoice"
                          >
                            <ArrowUpRight size={15} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center">
                    <div className="mx-auto max-w-sm rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-8">
                      <p className="text-sm font-medium text-slate-700">
                        No invoices match this view.
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Try another search term or switch the status filter.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </BillingSurface>
  );
};
